/**
 * Phase 9 — 1099-NEC vendor tracking.
 *
 * Aggregates calendar-year payments per vendor from APContext payments.
 * Pure selector — no writes. Threshold = $600 (IRS 1099-NEC).
 *
 * Caveat: 1099-NEC is for *services* paid to non-corporates. We rely on the
 * vendor's `is1099Eligible` flag (admin-curated) rather than parsing bill
 * line accounts. Corporate vendors should have `is1099Eligible = false`.
 */
import type { BillPayment } from "../ap/apTypes";
import type { Vendor } from "@/data/VendorPOContext";

export const IRS_1099_THRESHOLD = 600;

export interface Vendor1099Row {
  vendorId: string;
  vendorName: string;
  is1099Eligible: boolean;
  taxIdLast4?: string;
  w9Status: "not_requested" | "requested" | "on_file" | "expired";
  w9ReceivedAt?: string;
  ytdPaid: number;
  paymentCount: number;
  meetsThreshold: boolean;
  /** Eligible + meets threshold + missing W-9 — blocks 1099 issuance. */
  blocking: boolean;
}

/**
 * Convenience that resolves vendor on each payment by joining bills.
 */
export function build1099Rows(
  vendors: Vendor[],
  payments: BillPayment[],
  bills: Array<{ id: string; vendorId: string }>,
  year: number,
): Vendor1099Row[] {
  const start = new Date(Date.UTC(year, 0, 1)).toISOString();
  const end = new Date(Date.UTC(year + 1, 0, 1)).toISOString();
  const billVendor = new Map(bills.map(b => [b.id, b.vendorId]));

  const agg = new Map<string, { sum: number; count: number }>();
  for (const p of payments) {
    if (p.paidAt < start || p.paidAt >= end) continue;
    const vid = billVendor.get(p.billId);
    if (!vid) continue;
    const cur = agg.get(vid) ?? { sum: 0, count: 0 };
    cur.sum += p.amount;
    cur.count += 1;
    agg.set(vid, cur);
  }

  return vendors.map(v => {
    const a = agg.get(v.vendorId) ?? { sum: 0, count: 0 };
    const eligible = v.is1099Eligible === true;
    const w9 = v.w9Status ?? "not_requested";
    const meets = a.sum >= IRS_1099_THRESHOLD;
    return {
      vendorId: v.vendorId,
      vendorName: v.vendorName,
      is1099Eligible: eligible,
      taxIdLast4: v.taxIdLast4,
      w9Status: w9,
      w9ReceivedAt: v.w9ReceivedAt,
      ytdPaid: a.sum,
      paymentCount: a.count,
      meetsThreshold: meets,
      blocking: eligible && meets && w9 !== "on_file",
    };
  });
}
